import { useEffect, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

import {
  useSweepDefaults,
  useSystemConfig,
  useUpdateSystemConfig
} from '../../api/useSystemConfig';
import { SweepDualPostEditorDialog } from '../entry/SweepDualPostEditorDialog';
import { HEDGE_MODE_GLOBAL_OPTIONS, type HedgeMode } from './counterHedgeMode';

/**
 * Compact global-default controls for the counter-hedge pricing mode:
 *  - a Select for `counterHedgeModeDefault` (3 concrete modes)
 *  - a numeric TextField for `marketHedgeMaxSum` (step 0.01, min 0)
 *
 * Both write through `useUpdateSystemConfig` (POST /api/system/hedge-mode),
 * which invalidates the `['system-config']` query on success so the value
 * re-reads. The mode Select persists immediately; the max-sum field commits
 * on blur / Enter so the operator can type without firing a request per keypress.
 *
 * Renders nothing until the first `useSystemConfig` response lands.
 */
export function HedgeModeGlobalControls() {
  const { data: systemConfig } = useSystemConfig();
  const updateConfig = useUpdateSystemConfig();
  const { data: sweepDefaults } = useSweepDefaults();

  const [maxSum, setMaxSum] = useState('');
  const [sweepConfigOpen, setSweepConfigOpen] = useState(false);

  // Highlight the sweep config icon when ANY global sweep field is set
  // (non-null); grey when every field is still the engine default.
  const sweepConfigured =
    !!sweepDefaults &&
    Object.values(sweepDefaults).some(v => v !== null && v !== undefined);

  // Seed / re-sync the max-sum input from the server value whenever it lands
  // or changes, but not while a write is in flight (avoids clobbering the
  // operator's in-flight edit).
  useEffect(() => {
    if (systemConfig?.marketHedgeMaxSum == null) return;
    if (updateConfig.isLoading) return;
    setMaxSum(String(systemConfig.marketHedgeMaxSum));
  }, [systemConfig?.marketHedgeMaxSum, updateConfig.isLoading]);

  if (!systemConfig) return null;

  const handleModeChange = (next: HedgeMode) => {
    if (next === systemConfig.counterHedgeModeDefault) return;
    updateConfig.mutate({ counterHedgeModeDefault: next });
  };

  const commitMaxSum = () => {
    const raw = maxSum.trim();
    if (raw === '') {
      setMaxSum(String(systemConfig.marketHedgeMaxSum));
      return;
    }
    const v = Number.parseFloat(raw);
    if (!Number.isFinite(v) || v < 0) {
      setMaxSum(String(systemConfig.marketHedgeMaxSum));
      return;
    }
    if (v === systemConfig.marketHedgeMaxSum) return;
    updateConfig.mutate({ marketHedgeMaxSum: v });
  };

  const labelSx = {
    fontSize: '0.7rem',
    color: 'grey.500',
    whiteSpace: 'nowrap' as const
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography sx={labelSx}>Hedge</Typography>
      <Select
        size="small"
        value={systemConfig.counterHedgeModeDefault}
        disabled={updateConfig.isLoading}
        onChange={e => handleModeChange(e.target.value as HedgeMode)}
        inputProps={{ 'aria-label': 'global hedge mode default' }}
        sx={{
          fontSize: '0.7rem',
          '& .MuiSelect-select': { py: 0.3, px: 0.6 }
        }}
      >
        {HEDGE_MODE_GLOBAL_OPTIONS.map(o => (
          <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem' }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
      <Typography sx={labelSx}>Max sum</Typography>
      <TextField
        size="small"
        value={maxSum}
        disabled={updateConfig.isLoading}
        onChange={e => setMaxSum(e.target.value)}
        onBlur={commitMaxSum}
        onKeyDown={e => {
          if (e.key === 'Enter') commitMaxSum();
        }}
        inputProps={{
          'aria-label': 'market hedge max sum',
          inputMode: 'decimal',
          step: 0.01,
          min: 0,
          style: { width: 56, textAlign: 'right', fontSize: '0.7rem' }
        }}
        sx={{ '& .MuiInputBase-input': { py: 0.3, px: 0.5 } }}
      />
      {updateConfig.isLoading && <CircularProgress size={12} />}
      <Tooltip title="Config global de Sweep">
        <IconButton
          size="small"
          onClick={() => setSweepConfigOpen(true)}
          aria-label="global sweep config"
          sx={{
            p: 0.2,
            color: sweepConfigured ? 'warning.main' : 'grey.500'
          }}
        >
          <BoltOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <SweepDualPostEditorDialog
        open={sweepConfigOpen}
        onClose={() => setSweepConfigOpen(false)}
      />
    </Stack>
  );
}
